/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Box, Grid, Stack } from "@mui/material";
import { Method } from "axios";
import { H4 } from "components/generals/_Titles";
import FormikForm from "components/layout/FormikForm";
import { CoreAPI_URLs } from "constants/http.const";
import { Form, FormikProps } from "formik";
import useHttp from "hooks/http.hook";
import BadgeIcon from "@mui/icons-material/Badge";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import { FormProps } from "interfaces";
import { FacilityInterface, FacilityTypeEnum, HeaderEnum, ResourceInterface } from "@malmur-technologies/medicalsystem-shared";
import { useTranslation } from "react-i18next";
import { EntityPermissions, FormActionType, PermissionsUser, SelectArrayType } from "types";
import {
  facilityFormYUPValidation,
  getSelectOptions,
  validatePhoneInputSymbol,
} from "utils/form.utils";
import FormField from "components/generals/FormInput";
import FormSelectInput from "components/generals/FormSelectInput";
import Button from "components/generals/Button";
import FormActionButtons from "./custom/FormActionButtons";
import DatePicker from "components/generals/DatePicker";
import { useMemo, useState } from "react";
import FormSwitch from "components/generals/Switch";
import FormPhoneInput from "components/generals/FormPhoneInput";
import PermissionWrapper from "components/generals/PermissionWrapper";
import ResourceListModal from "components/views/ResourceListModal";
import { useAppDispatch } from "store/store";
import EventSlice from "store/slices/event.slice";

/**
 * Medical facility management form with resource assignment.
 * 
 * Features:
 * - Create and edit medical facility records
 * - Facility type selection (hospital, clinic, lab, pharmacy, etc.)
 * - Complete address information (street, city, state, ZIP, country)
 * - Contact details (phone, fax, email)
 * - Operating hours configuration
 * - Active/inactive status toggle
 * - Resource assignment modal:
 *   - Link medical resources to facility
 *   - Manage equipment and room allocations
 * - Date tracking (created, last modified)
 * - Form validation with Yup schema
 * - Phone number format validation
 * - Permission-based save/delete actions
 * - View-only mode support
 * 
 * Facility Types:
 * - Hospital, Clinic, Urgent Care
 * - Laboratory, Imaging Center
 * - Pharmacy, Surgery Center
 * - Office, Other
 * 
 * @param model - Facility data to edit (or new facility template)
 * @param action - Form action (NEW, MODIFY, READ_ONLY)
 * @param onCloseAction - Callback when form is closed
 * @param setModelList - Callback to update facility list
 * @param modelList - Current list of facilities
 * @returns Facility management form with resource integration
 */
const FacilityForm: React.FC<FormProps<FacilityInterface>> = ({
  model,
  action,
  onCloseAction,
  setModelList,
  modelList,
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const { t } = useTranslation();
  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { addErrorMessage } = EventSlice.actions;

  const isViewMode = action === FormActionType.READ_ONLY;

  const facilityListOptions = useMemo<SelectArrayType[]>(
    () => getSelectOptions(FacilityTypeEnum, t),
    []
  );
  const getFacilityTypeText = (typeValue: any) => {
    const option = facilityListOptions.find(opt => opt.value === typeValue);
    return option ? option.label : typeValue;
  };

  const submitHandler = (model: Partial<FacilityInterface>) => {
    const method: Method = action === FormActionType.NEW ? "POST" : "PUT";
    const successMessageText = action === FormActionType.NEW ? t("facilityForm.facilityCreated") : t("facilityForm.facilityUpdated");

    if (model.phone) {
      model.phone = validatePhoneInputSymbol(model.phone);
    }

    if (model.fax) {
      model.fax = validatePhoneInputSymbol(model.fax);
    }

    if (!model.email?.length) {
      model.email = undefined;
    }
    sendRequest(
      CoreAPI_URLs.FACILITY_GENERAL_OPERATIONS,
      onFacilityUpdateHandler,
      method,
      JSON.stringify(model),
      [{ header: HeaderEnum.RESOURCE_ID, value: model.id }],
      { successMessage: successMessageText }
    );
  };

  function cleanResourceForBatch(resource: Partial<ResourceInterface> & { isNew?: boolean }, facilityId: string): Partial<ResourceInterface> {
    const cleaned: Partial<ResourceInterface> = {
      name: resource.name,
      code: resource.code,
    };

    // Only include phone if it has a non-empty value
    if (resource.phone && resource.phone.trim() !== "") {
      cleaned.phone = resource.phone;
    }

    // Only include location if facilityId is provided
    if (facilityId) {
      cleaned.location = { id: facilityId };
    }

    // Only include defaultAppointmentProvider if it has a valid id
    if (resource.defaultAppointmentProvider?.id) {
      cleaned.defaultAppointmentProvider = { id: resource.defaultAppointmentProvider.id };
    }

    return cleaned;
  }

  const onFacilityUpdateHandler = (facility: FacilityInterface) => {
    if (action === FormActionType.MODIFY) {
      setModelList(modelList.map((model) => (model.id === facility.id ? facility : model)));
    } else {
      setModelList((oldArray) => [...oldArray, facility]);
    }
    onCloseAction();
  };

  const handleSaveResources = async (resources: Partial<ResourceInterface>[]) => {
    if (!model.id && action === FormActionType.NEW) {
      dispatch(addErrorMessage({ message: t('resourceCart.saveFacilityFirst'), id: crypto.randomUUID() }));
      return;
    }

    setResourcesLoading(true);
    try {
      const resourcesForBatch = resources.map(resource => cleanResourceForBatch(resource, model.id));
      const batchPayload = {
        resources: resourcesForBatch
      };

      const createdResources = await sendRequest(
        CoreAPI_URLs.RESOURCE_BATCH_CREATE,
        null,
        "POST",
        JSON.stringify(batchPayload)
      );

    } catch (error) {
      console.error('Error creating resources batch:', error);
      dispatch(addErrorMessage({ message: t('resourceCart.errorCreatingResources'), id: crypto.randomUUID() }));
    } finally {
      setResourcesLoading(false);
      setIsResourceModalOpen(false);
    }
  };

  const canManageResources = action === FormActionType.MODIFY && model.id && !isViewMode;

  return (
    <>
      <Box sx={{ maxWidth: '60%', mx: 'auto', px: 2 }}>
        <FormikForm
          onSubmit={(model) => {
            submitHandler(model);
          }}
          initialValues={model}
          validationSchema={!isViewMode ? facilityFormYUPValidation(t) : undefined}
        >
          {({ values, errors, touched, setFieldValue }: FormikProps<Partial<FacilityInterface>>) => (
            <Form id="facilityForm" autoComplete="true">
              <Stack alignItems="center" direction="row" gap={2} mb={3}>
                <BadgeIcon fontSize="large" />
                <H4>{t("facilityForm.facilityFormTitle")}</H4>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6} lg={6}>
                  <FormField
                    type="text"
                    label={t("facilityForm.name")}
                    name="name"
                    value={values.name || ""}
                    error={errors.name && touched.name && !isViewMode}
                    readOnly={isViewMode}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  {isViewMode ? (
                    <FormField
                      type="text"
                      label={t("facilityForm.type")}
                      name="type"
                      value={getFacilityTypeText(values.type) || ""}
                      readOnly={true}
                    />
                  ) : (
                    <FormSelectInput
                      name="type"
                      label={t("facilityForm.type")}
                      selectArray={facilityListOptions}
                      defaultValue={values.type}
                    />
                  )}
                </Grid>
                <PermissionWrapper
                  requiredPermissions={[PermissionsUser[EntityPermissions.FACILITY].VIEW]}
                >
                  <Grid item xs={12} md={3} lg={3}>
                    <FormField
                      type="text"
                      label={t("facilityForm.code")}
                      name="code"
                      value={values.code || ""}
                      error={errors.code && touched.code && !isViewMode}
                      readOnly={isViewMode}
                    />
                  </Grid>
                </PermissionWrapper>

                <Grid item xs={12} md={6} lg={6} mb={2}>
                  {isViewMode ? (
                    <FormField
                      type="text"
                      label={t("facilityForm.primaryPracticeFlag")}
                      name="primaryPracticeFlagDisplay"
                      value={values.primaryPracticeFlag ? t("common.yes") : t("common.no")}
                      readOnly={true}
                    />
                  ) : (
                    <FormSwitch
                      label={t("facilityForm.primaryPracticeFlag")}
                      value={values.primaryPracticeFlag}
                      name="primaryPracticeFlag"
                      setFieldValue={setFieldValue}
                    />
                  )}
                </Grid>

                <Grid item xs={12}>
                  <FormField
                    type="text"
                    label={t("facilityForm.address")}
                    name="address"
                    value={values.address || ""}
                    error={errors.address && touched.address && !isViewMode}
                    readOnly={isViewMode}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4} lg={4}>
                  <FormField
                    type="text"
                    label={t("facilityForm.city")}
                    name="city"
                    value={values.city || ""}
                    error={errors.city && touched.city && !isViewMode}
                    readOnly={isViewMode}
                  />
                </Grid>
                <Grid item xs={12} md={4} lg={4}>
                  <FormField
                    type="text"
                    label={t("facilityForm.state")}
                    name="state"
                    value={values.state || ""}
                    error={errors.state && touched.state && !isViewMode}
                    readOnly={isViewMode}
                  />
                </Grid>
                <Grid item xs={12} md={3} lg={3} mb={4}>
                  <FormField
                    type="text"
                    label={t("facilityForm.zip")}
                    name="zip"
                    value={values.zip || ""}
                    error={errors.zip && touched.zip && !isViewMode}
                    readOnly={isViewMode}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} md={6} lg={6}>
                  <FormField
                    type="text"
                    label={t("facilityForm.email")}
                    name="email"
                    value={values.email || ""}
                    error={errors.email && touched.email && !isViewMode}
                    readOnly={isViewMode}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={12} lg={6}>
                  {isViewMode ? (
                    <FormField
                      type="text"
                      label={t("facilityForm.phone")}
                      name="phone"
                      value={values.phone || ""}
                      readOnly={true}
                    />
                  ) : (
                    <FormPhoneInput
                      inputClass="phone"
                      specialLabel={t("facilityForm.phone")}
                      country={"us"}
                      name="phone"
                      required={true}
                      value={values.phone}
                      onChange={(value) => {
                        values.phone = value;
                      }}
                    />
                  )}
                </Grid>
                <PermissionWrapper
                  requiredPermissions={[PermissionsUser[EntityPermissions.FACILITY].VIEW]}
                >
                  <Grid item xs={12} md={12} lg={6}>
                    {isViewMode ? (
                      <FormField
                        type="text"
                        label={t("facilityForm.fax")}
                        name="fax"
                        value={values.fax || ""}
                        readOnly={true}
                      />
                    ) : (
                      <FormPhoneInput
                        inputClass="fax"
                        specialLabel={t("facilityForm.fax")}
                        country={"us"}
                        name="fax"
                        required={false}
                        value={values.fax}
                        onChange={(value) => {
                          values.fax = value;
                        }}
                      />
                    )}
                  </Grid>
                </PermissionWrapper>
              </Grid>

              <Grid item xs={12} md={6} lg={6}>
                <FormField
                  type="text"
                  label={t("facilityForm.notes")}
                  name="notes"
                  value={values.notes || ""}
                  error={errors.notes && touched.notes && !isViewMode}
                  readOnly={isViewMode}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  direction={{ xs: "column", sm: "row" }}
                  gap={2}
                >
                  {isViewMode ? (
                    <FormField
                      type="text"
                      label={t("facilityForm.startDate")}
                      name="startDate"
                      value={values.startDate ? new Date(values.startDate).toLocaleDateString() : ""}
                      readOnly={true}
                    />
                  ) : (
                    <DatePicker
                      id="cy-startDate-picker"
                      name="startDate"
                      value={values.startDate}
                      label={t("facilityForm.startDate")}
                      error={errors.startDate && touched.startDate}
                      options={{
                        dateFormat: "Y-m-d",
                      }}
                      onChange={(selectedDate) => {
                        values.startDate = selectedDate[0];
                      }}
                    />
                  )}
                </Stack>
              </Grid>

              <br></br>

              {canManageResources && (
                <Grid item xs={12} mb={2}>
                  <PermissionWrapper
                    requiredPermissions={[PermissionsUser[EntityPermissions.RESOURCE].CREATE]}
                  >
                    <Button
                      id="cy-manage-resources-button"
                      color="info"
                      startIcon={<AddBusinessIcon />}
                      onClick={() => setIsResourceModalOpen(true)}
                      disabled={resourcesLoading}
                    >
                      {t("facilityForm.manageResources")}
                    </Button>
                  </PermissionWrapper>
                </Grid>
              )}
              <Grid container spacing={2} className="form-container-action-buttons">
                {!isViewMode && (
                  <Grid item xs={12} sm="auto" md="auto">
                    {action === FormActionType.NEW ? (
                      <PermissionWrapper
                        requiredPermissions={[PermissionsUser[EntityPermissions.FACILITY].CREATE]}
                      >
                        <Button id="cy-submit-button" type="submit" variant="contained" color="info" disabled={isLoading}>
                          {t("stepperFormActionButtons.submit")}
                        </Button>
                      </PermissionWrapper>
                    ) : (
                      <PermissionWrapper
                        requiredPermissions={[PermissionsUser[EntityPermissions.FACILITY].EDIT]}
                      >
                        <Button id="cy-update-button" type="submit" variant="contained" color="info" disabled={isLoading}>
                          {t("stepperFormActionButtons.update")}
                        </Button>
                      </PermissionWrapper>
                    )}
                  </Grid>
                )}
                <Grid item xs={12} sm="auto" md="auto">
                  <Button
                    id="cy-close-button"
                    variant="contained"
                    color="secondary"
                    onClick={onCloseAction}
                  >
                    {t("common.close")}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </FormikForm>
      </Box >
      {!isViewMode && (
        <ResourceListModal
          open={isResourceModalOpen}
          onClose={() => setIsResourceModalOpen(false)}
          facilityId={model.id}
          facilityName={model.name}
          onSaveResources={handleSaveResources}
        />
      )
      }
    </>
  );
};

export default FacilityForm;